import React, { useState, useEffect } from 'react';
import { ChevronRight, Phone, Mail, MapPin, Bug, MessageCircle, Send, CheckCircle, Zap, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GamifiedContactForm = () => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    pestType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [completionScore, setCompletionScore] = useState(0);
  const [quickCallRequested, setQuickCallRequested] = useState(false);

  const totalSteps = 4;

  const stepConfig = [
    {
      step: 1,
      title: "👋 Qui êtes-vous ?",
      subtitle: "Présentez-vous rapidement",
      emoji: "🧑‍💼",
      fields: ['firstName', 'lastName'],
      reward: 25
    },
    {
      step: 2,
      title: "📞 Comment vous joindre ?",
      subtitle: "Pour une réponse ultra-rapide",
      emoji: "📱",
      fields: ['email', 'phone'],
      reward: 25
    },
    {
      step: 3,
      title: "🎯 Votre problème ?",
      subtitle: "Aidez-nous à mieux vous aider",
      emoji: "🔍",
      fields: ['location', 'pestType'],
      reward: 25
    },
    {
      step: 4,
      title: "💬 Décrivez la situation",
      subtitle: "Plus c'est précis, mieux on intervient",
      emoji: "📝",
      fields: ['message'],
      reward: 25
    }
  ];

  // Calculate completion percentage
  useEffect(() => {
    const filledFields = Object.values(formData).filter(value => value.trim() !== '').length;
    const totalFields = Object.keys(formData).length - 1; // Excluding phone which is optional
    setCompletionScore(Math.round((filledFields / totalFields) * 100));
  }, [formData]);

  const currentStepConfig = stepConfig[currentStep - 1];
  const isStepComplete = currentStepConfig.fields.every(field => 
    field === 'phone' ? true : formData[field].trim() !== ''
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps && isStepComplete) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleQuickCall = () => {
    setQuickCallRequested(true);
    toast({
      title: "🚀 Demande de rappel enregistrée !",
      description: "Nous vous appelons dans les 10 minutes !",
    });
    
    // Simulate quick call request
    setTimeout(() => {
      window.location.href = 'tel:0676583038';
    }, 2000);
  };

  const handleSubmit = async () => {
    if (!isStepComplete) return;

    setIsSubmitting(true);

    try {
      const apiData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        location: formData.location,
        pest_type: formData.pestType,
        message: formData.message
      };

      const response = await axios.post(`${BACKEND_URL}/api/contact`, apiData);

      if (response.data.status === 'success') {
        setIsComplete(true);
        toast({
          title: "🎉 Mission accomplie !",
          description: "Votre demande est transmise ! Réponse sous 1h.",
        });
      }
    } catch (error) {
      toast({
        title: "❌ Erreur",
        description: "Une erreur est survenue. Essayez d'appeler directement !",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (quickCallRequested) {
    return (
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardContent className="p-8 text-center">
          <div className="animate-bounce mb-4">
            <Phone className="h-16 w-16 text-green-600 mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-green-800 mb-4">
            📞 Rappel en cours !
          </h3>
          <p className="text-green-700 mb-6">
            Notre expert vous appelle dans les prochaines minutes.<br />
            Gardez votre téléphone à portée !
          </p>
          <div className="animate-pulse">
            <div className="h-2 bg-green-300 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full w-full transition-all duration-2000"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isComplete) {
    return (
      <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
        <CardContent className="p-8 text-center">
          <div className="animate-bounce mb-6">
            <CheckCircle className="h-20 w-20 text-yellow-600 mx-auto" />
          </div>
          <h3 className="text-3xl font-bold text-yellow-800 mb-4">
            🏆 Mission accomplie !
          </h3>
          <div className="bg-white/80 rounded-full px-6 py-3 inline-block mb-6">
            <span className="text-2xl font-bold text-yellow-700">{completionScore}% complété</span>
          </div>
          <p className="text-yellow-700 text-lg mb-6">
            Votre demande d'intervention a été transmise !<br />
            <strong>Notre équipe vous contacte sous 1 heure.</strong>
          </p>
          <div className="space-y-4">
            <Button
              onClick={() => window.location.href = 'tel:0676583038'}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold px-8 py-4 text-lg"
            >
              <Phone className="h-5 w-5 mr-2" />
              En cas d'urgence : 06 76 58 30 38
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50">
      {/* Progress Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold">Formulaire Express ⚡</h3>
          <div className="text-right">
            <div className="text-3xl font-bold">{completionScore}%</div>
            <div className="text-sm opacity-90">Progression</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="bg-white/30 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-white h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between mt-2 text-sm">
          {stepConfig.map((step, index) => (
            <div 
              key={step.step}
              className={`text-center transition-all duration-200 ${
                currentStep >= step.step ? 'opacity-100 scale-110' : 'opacity-60'
              }`}
            >
              <div className="text-lg">{step.emoji}</div>
            </div>
          ))}
        </div>
      </div>

      <CardContent className="p-8">
        {/* Step Content */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">{currentStepConfig.emoji}</div>
            <h4 className="text-2xl font-bold text-gray-900 mb-2">
              {currentStepConfig.title}
            </h4>
            <p className="text-gray-600">{currentStepConfig.subtitle}</p>
          </div>

          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    placeholder="Votre prénom *"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="text-lg py-3"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Votre nom *"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="text-lg py-3"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact Info */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <Input
                type="email"
                placeholder="Votre email *"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="text-lg py-3"
              />
              <Input
                type="tel"
                placeholder="Votre téléphone (optionnel)"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="text-lg py-3"
              />
              
              {/* Quick Call Option */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-blue-800">🚀 Option Turbo</div>
                    <div className="text-sm text-blue-600">On vous rappelle dans 10 minutes</div>
                  </div>
                  <Button
                    onClick={handleQuickCall}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Me rappeler
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Problem Details */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <Input
                placeholder="Où se trouve le problème ? (ville, adresse) *"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="text-lg py-3"
              />
              
              <Select value={formData.pestType} onValueChange={(value) => handleInputChange('pestType', value)}>
                <SelectTrigger className="text-lg py-3">
                  <SelectValue placeholder="Quel type de nuisible ? *" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="frelon-asiatique">🐝 Frelon asiatique (URGENT)</SelectItem>
                  <SelectItem value="frelon-europeen">🐛 Frelon européen</SelectItem>
                  <SelectItem value="guepe">🐞 Guêpes</SelectItem>
                  <SelectItem value="bourdon">🐝 Bourdons</SelectItem>
                  <SelectItem value="autre">🦟 Autre nuisible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 4: Message */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <Textarea
                placeholder="Décrivez votre situation : où est le nid ? Quelle taille ? Y a-t-il du danger immédiat ? *"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={5}
                className="text-lg"
              />
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Gift className="h-6 w-6 text-green-600 mt-1" />
                  <div>
                    <div className="font-semibold text-green-800">🎁 Bonus intervention</div>
                    <div className="text-sm text-green-600">
                      Formulaire complet = Devis gratuit + Conseils préventifs offerts !
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            onClick={prevStep}
            variant="outline"
            disabled={currentStep === 1}
            className="flex items-center space-x-2"
          >
            <span>← Retour</span>
          </Button>

          <div className="text-center">
            <div className="text-sm text-gray-500">Étape {currentStep} sur {totalSteps}</div>
            {isStepComplete && (
              <div className="text-green-600 font-medium text-sm">✓ Étape validée</div>
            )}
          </div>

          {currentStep < totalSteps ? (
            <Button
              onClick={nextStep}
              disabled={!isStepComplete}
              className={`flex items-center space-x-2 ${
                isStepComplete 
                  ? 'bg-yellow-500 hover:bg-yellow-600 text-black animate-pulse' 
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              <span>Suivant</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepComplete || isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2 transform hover:scale-105 transition-all duration-200"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Envoyer 🚀</span>
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>

      {/* Floating Elements */}
      <div className="absolute top-4 right-4 w-2 h-2 bg-yellow-400 rounded-full animate-ping opacity-60"></div>
      <div className="absolute bottom-6 left-6 w-1 h-1 bg-blue-400 rounded-full animate-pulse"></div>
    </Card>
  );
};

export default GamifiedContactForm;